import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GraduationCap, Briefcase, Code2, Sparkles } from 'lucide-react';
import ScreenHeading from '../../utilities/ScreenHeading/ScreenHeading';
import ScrollService from '../../utilities/ScrollService';
import Animations from '../../utilities/Animations';
import './Resume.css';

const resumeBullets = [
  { label: 'Education', subLabel: 'Schools & Degrees', Icon: GraduationCap },
  { label: 'Work History', subLabel: 'Career Timeline', Icon: Briefcase },
  {
    label: 'Programming Skills',
    subLabel: 'Languages & Frameworks',
    Icon: Code2,
  },
  { label: 'Interests', subLabel: 'Beyond the Code', Icon: Sparkles },
];

const programmingSkillsDetails = [
  { skill: 'Typescript', ratingPercentage: 85 },
  { skill: 'JavaScript', ratingPercentage: 85 },
  { skill: 'Python', ratingPercentage: 85 },
  { skill: 'C++', ratingPercentage: 85 },
  { skill: 'NextJS', ratingPercentage: 85 },
  { skill: 'SQL', ratingPercentage: 85 },
  { skill: 'React JS', ratingPercentage: 85 },
  { skill: 'Tailwind UI', ratingPercentage: 85 },
  { skill: 'Express JS', ratingPercentage: 85 },
  { skill: 'Node JS', ratingPercentage: 85 },
  { skill: 'Mongo DB', ratingPercentage: 85 },
  { skill: 'PostgreSQL', ratingPercentage: 85 },
  { skill: 'SASS / SCSS ', ratingPercentage: 70 },
  { skill: 'HTML', ratingPercentage: 85 },
  { skill: 'CSS', ratingPercentage: 85 },
];

// Staggered spring bar fill — panel is only mounted when active (AnimatePresence),
// so initial: {width:0} always fires fresh on every tab visit.
const ProgrammingSkillsPanel = () => (
  <div className="resume-screen-container programming-skills-container">
    {programmingSkillsDetails.map((skill, index) => (
      <div className="skill-parent" key={skill.skill}>
        <span>{skill.skill}</span>
        <div className="skill-percentage">
          <motion.div
            className="active-percentage-bar"
            initial={{ width: 0 }}
            animate={{ width: skill.ratingPercentage + '%' }}
            transition={{
              type: 'spring',
              bounce: 0.15,
              duration: 1.1,
              delay: index * 0.045,
            }}
          />
        </div>
      </div>
    ))}
  </div>
);

// Vertical timeline entry — adapted from  v-timeline architecture.
// Circle icon sits on a connecting line; entries with descriptions are collapsible.
const TimelineEntry = ({
  title,
  subTitle,
  fromDate,
  toDate,
  description,
  redirect,
  defaultOpen = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const dateLabel = [fromDate, toDate].filter(Boolean).join(' – ');
  const hasDescription = Boolean(description);

  return (
    <li className="timeline-entry">
      {/* Left column: circle toggle + vertical connector */}
      <div className="timeline-left">
        <button
          type="button"
          onClick={() => hasDescription && setOpen((o) => !o)}
          className={`timeline-dot ${open || !hasDescription ? 'timeline-dot--active' : ''}`}
          style={{ cursor: hasDescription ? 'pointer' : 'default' }}
          aria-expanded={hasDescription ? open : undefined}
        >
          {hasDescription ? (open ? '−' : '+') : '•'}
        </button>
        <div className="timeline-connector" />
      </div>

      {/* Right column: heading + optional collapsible content */}
      <div className="timeline-content">
        <div className="timeline-header">
          <div className="timeline-title-row">
            <span className="bullet-heading">{title ?? ''}</span>
            {redirect && (
              <a
                href={redirect}
                target="_blank"
                rel="noreferrer"
                aria-label="View external link"
              >
                <svg
                  className="pl-1"
                  width="16px"
                  height="16px"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ fill: 'var(--dark-orange)' }}
                >
                  <path d="M18 20.75H6C5.27 20.75 4.57 19.44 3.54 19.43 3.25 18.73 3.25 18V6C3.25 5.27 3.54 4.57 4.06 4.06C4.57 3.54 5.27 3.25 6 3.25H12C12.2 3.25 12.39 3.33 12.53 3.47C12.67 3.61 12.75 3.8 12.75 4C12.75 4.2 12.67 4.39 12.53 4.53C12.39 4.67 12.2 4.75 12 4.75H6C5.67 4.75 5.35 4.88 5.12 5.12C4.88 5.35 4.75 5.67 4.75 6V18C4.75 18.33 4.88 18.65 5.12 18.88C5.35 19.12 5.67 19.25 6 19.25H18C18.33 19.25 18.65 19.12 18.88 18.88C19.12 18.65 19.25 18.33 19.25 18V12C19.25 11.8 19.33 11.61 19.47 11.47C19.61 11.33 19.8 11.25 20 11.25C20.2 11.25 20.39 11.33 20.53 11.47C20.67 11.61 20.75 11.8 20.75 12V18C20.75 18.73 20.46 19.43 19.94 19.94C19.43 20.46 18.73 20.75 18 20.75Z" />
                  <path d="M20 8.75C19.8 8.75 19.61 8.67 19.47 8.53C19.33 8.39 19.25 8.2 19.25 8V4.75H16C15.8 4.75 15.61 4.67 15.47 4.53C15.33 4.39 15.25 4.2 15.25 4C15.25 3.8 15.33 3.61 15.47 3.47C15.61 3.33 15.8 3.25 16 3.25H20C20.2 3.25 20.39 3.33 20.53 3.47C20.67 3.61 20.75 3.8 20.75 4V8C20.75 8.2 20.67 8.39 20.53 8.53C20.39 8.67 20.2 8.75 20 8.75Z" />
                  <path d="M13.5 11.25C13.31 11.24 13.13 11.15 13 11C12.88 10.86 12.81 10.68 12.81 10.5C12.81 10.32 12.88 10.14 13 10L19.5 3.5C19.57 3.43 19.65 3.37 19.74 3.33C19.84 3.29 19.93 3.26 20.04 3.26C20.14 3.26 20.24 3.28 20.33 3.32C20.42 3.35 20.51 3.41 20.58 3.48C20.65 3.55 20.71 3.64 20.74 3.73C20.78 3.82 20.8 3.92 20.8 4.02C20.8 4.13 20.77 4.22 20.73 4.32C20.69 4.41 20.63 4.49 20.56 4.56L14 11C13.87 11.15 13.69 11.24 13.5 11.25Z" />
                </svg>
              </a>
            )}
          </div>
          {dateLabel && <div className="heading-date">{dateLabel}</div>}
        </div>

        {subTitle && (
          <div className="resume-sub-heading">
            <span>{subTitle}</span>
          </div>
        )}

        {hasDescription && (
          <div
            className={`timeline-description ${open ? 'timeline-description--open' : ''}`}
          >
            <span className="resume-description-text">{description}</span>
          </div>
        )}
      </div>
    </li>
  );
};

export default function Resume(props) {
  const [selectedBulletIndex, setSelectedBulletIndex] = useState(0);

  let fadeInScreenHandler = (screen) => {
    if (screen.fadeInScreen !== props.id) return;
    Animations.animations.fadeInScreen(props.id);
  };
  ScrollService.currentScreenFadeIn.subscribe(fadeInScreenHandler);

  const resumeDetails = [
    <ul className="timeline-list resume-screen-container" key="education">
      <TimelineEntry
        title="Northern Illinois University"
        subTitle="B.S. Computer Science · 4.0 GPA"
        fromDate="2025"
        toDate="2027"
      />
      <TimelineEntry
        title="Fullstack Academy of Code"
        subTitle="Software Engineering & Web Development"
      />
      <TimelineEntry
        title="Waubonsee Community College"
        subTitle="Associate of Science (A.S.)"
      />
    </ul>,

    <ul className="timeline-list resume-screen-container" key="work-experience">
      <TimelineEntry
        title="CoreBridge Systems"
        subTitle="Founding Software Engineer"
        fromDate="2026"
        toDate="Present"
        description="Building full-stack products across software, cloud, and AI — from backend services to the web app."
        redirect="https://www.corebridgesystems.io/"
        defaultOpen
      />
      <TimelineEntry
        title="Reality AI Lab"
        subTitle="Software Engineer"
        fromDate="Nov 2024"
        toDate="May 2025"
        description="JavaScript · React · Python · Docker · Google Cloud · Node.js · Firebase"
      />
      <TimelineEntry
        title="Ricoma International"
        subTitle="Developer"
        fromDate="2022"
        toDate="2024"
        description="Vue.js · React · PHP · Laravel · MySQL · HubSpot · Astro · Tailwind"
      />
      <TimelineEntry
        title="K.T. Billing"
        subTitle="Co-Founder & Systems / IT Administrator"
        fromDate="2016"
        toDate="2022"
        description="Systems and IT administration — networks, hardware, and internal tooling."
      />
    </ul>,

    <ProgrammingSkillsPanel key="programming-skills" />,

    <ul className="timeline-list resume-screen-container" key="interests">
      <TimelineEntry
        title="Martial Artist"
        description="Taekwondo Instructor Intern and Hapkido Martial Arts"
        defaultOpen
      />
      <TimelineEntry
        title="Cooking"
        description="I'm very big on grilling especially smoking ribs Memphis style"
        defaultOpen
      />
    </ul>,
  ];

  const handleCarousel = (index) => {
    setSelectedBulletIndex(index);
  };

  const getBullets = () => {
    return resumeBullets.map(({ label, subLabel, Icon }, index) => (
      <div
        key={label}
        onClick={() => handleCarousel(index)}
        onKeyDown={(e) => e.key === 'Enter' && handleCarousel(index)}
        role="button"
        tabIndex={0}
        className={`nav-bullet ${index === selectedBulletIndex ? 'nav-bullet--active' : ''}`}
        title={label}
      >
        <div className="nav-bullet-icon">
          <Icon size={20} />
        </div>
        <div className="nav-bullet-text">
          <span className="nav-bullet-label">{label}</span>
          <span className="nav-bullet-sublabel">{subLabel}</span>
        </div>
      </div>
    ));
  };

  const getResumeScreen = () => (
    <AnimatePresence mode="wait">
      <motion.div
        key={selectedBulletIndex}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
      >
        {resumeDetails[selectedBulletIndex]}
      </motion.div>
    </AnimatePresence>
  );

  return (
    <div
      className="resume-container screen-container fade-in"
      id={props.id || ''}
    >
      <div className="resume-content">
        <ScreenHeading title={'Resume'} subHeading={'My Bio Details'} />
        <div className="resume-card">
          <div className="resume-bullets">
            <div className="nav-bullets-container">{getBullets()}</div>
          </div>
          <div className="resume-bullet-details">{getResumeScreen()}</div>
        </div>
      </div>
    </div>
  );
}
